import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import TextTypography from 'src/app/components/shared/Typography/Text';
import CrossIcon from 'src/assets/icons/crossIcon';
import { Button, Dropdown, Space } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { MenuProps } from 'antd/lib';
import { ITableFilters } from '@interfaces';
import { useLeadContext } from '@contexts';
import { CustomerStatusEnumerator, LeadStatusEnum, SegmentEnumerator } from '@rasayi-workspace/shared';
import { City, State } from 'country-state-city';
import SelectInput from '../select';
import DateSelect from '../dateSelect';
import QuantitySelect from '../QuantityInput.tsx';

interface ISelectedFilterProps {
    values: ITableFilters;
    setvalues: (values: ITableFilters) => void;
}

const SelectedFilters = ({ values, setvalues }: ISelectedFilterProps) => {
    const { agents, productType, channels, brands } = useLeadContext();

    const cities = useMemo(() => {
        return City.getAllCities();
    }, [City]);

    const provinceCities = useMemo(() => {
        return City.getAllCities().map(state => ({
            value: state.name,
            label: state.name,
        }));
    }, []);

    const pakStates = useMemo(
        () =>
            State.getStatesOfCountry('PK').map(state => {
                return {
                    value: state.name,
                    label: state.name,
                };
            }),
        [State]
    );

    const [filters, setFilters] = useState<ITableFilters>({
        agent: [],
        status: [],
        city: [],
        segment: [],
        productType: [],
        channel: [],
        quantity: null,
        brand: [],
        createdAt: null,
        searchedText: '',
        province: [],
        customerStatus: [],
    });

    useEffect(() => {
        setFilters(values);
    }, [values]);

    const handleApply = () => {
        setvalues(filters);
    };

    const handleClearFilter = (key: keyof ITableFilters) => {
        setFilters(prevFilters => {
            const updatedValue = Array.isArray(prevFilters[key]) ? [] : null;

            // Update values at the same time
            setvalues({
                ...prevFilters,
                [key]: updatedValue,
            });

            return {
                ...prevFilters,
                [key]: updatedValue,
            };
        });
    };

    const handleClearAll = () => {
        setFilters({
            agent: [],
            status: [],
            city: [],
            province: [],
            segment: [],
            productType: [],
            channel: [],
            quantity: null,
            brand: [],
            createdAt: null,
            searchedText: '',
            customerStatus: [],
        });

        setvalues({
            agent: [],
            status: [],
            city: [],
            segment: [],
            productType: [],
            channel: [],
            quantity: null,
            brand: [],
            createdAt: null,
            searchedText: '',
            province: [],
            customerStatus: [],
        });
    };
    const isFilterApllied = useMemo(
        () =>
            filters.agent.length ||
            filters.status.length ||
            filters.city.length ||
            filters.segment.length ||
            filters.productType.length ||
            filters.channel.length ||
            filters.brand.length ||
            filters.quantity !== null ||
            filters.createdAt,
        [filters]
    );
    const selectedAgents = useMemo(() => {
        return values.agent.map(selectedValue => agents.find(_value => selectedValue === _value.id)?.name || '');
    }, [agents, values.agent]);

    const selectedBrands = useMemo(() => {
        return values.brand.map(selectedValue => brands.find(_value => selectedValue === _value.id)?.name || '');
    }, [brands, values.brand]);

    const selectedChannels = useMemo(() => {
        return values.channel.map(selectedValue => channels.find(_value => selectedValue === _value.id)?.name || '');
    }, [channels, values.channel]);

    const selectedProductType = useMemo(() => {
        return values.productType.map(selectedValue => productType.find(_value => selectedValue === _value.id)?.name || '');
    }, [productType, values.productType]);

    return (
        <div className="flex flex-wrap gap-2 items-center  p-2 rounded-md">
            {filters.agent.length > 0 && (
                <FilterItem handleApply={handleApply} label="Agent" onClear={() => handleClearFilter('agent')} value={selectedAgents}>
                    <SelectInput
                        label="Agent"
                        options={agents?.map(obj => ({ label: obj.name, value: obj.id }))}
                        placeholder="Search Agent"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={false}
                        value={filters.agent}
                        onChange={val => setFilters(prev => ({ ...prev, agent: val }))}
                        mode="multiple"
                    />{' '}
                </FilterItem>
            )}
            {filters.status.length > 0 && (
                <FilterItem
                    handleApply={handleApply}
                    label="Status"
                    onClear={() => handleClearFilter('status')}
                    value={values.status.map(selectedValue => Object.values(LeadStatusEnum).find(_value => selectedValue === _value) || '')}
                >
                    <SelectInput
                        label="Status"
                        options={Object.values(LeadStatusEnum).map(status => ({ label: status, value: status }))}
                        placeholder="Search Status"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={false}
                        value={filters.status}
                        onChange={val => setFilters(prev => ({ ...prev, status: val }))}
                        mode="multiple"
                    />
                </FilterItem>
            )}

            {filters.customerStatus.length > 0 && (
                <FilterItem
                    handleApply={handleApply}
                    label="Status"
                    onClear={() => handleClearFilter('customerStatus')}
                    value={values.customerStatus.map(selectedValue => Object.values(CustomerStatusEnumerator).find(_value => selectedValue === _value) || '')}
                >
                    <SelectInput
                        label="Status"
                        options={Object.values(CustomerStatusEnumerator).map(status => ({ label: status, value: status }))}
                        placeholder="Search Status"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={false}
                        value={filters.customerStatus}
                        onChange={val => setFilters(prev => ({ ...prev, customerStatus: val }))}
                        mode="multiple"
                    />
                </FilterItem>
            )}

            {filters.city.length > 0 && (
                <FilterItem
                    handleApply={handleApply}
                    label="City"
                    onClear={() => handleClearFilter('city')}
                    value={values.city.map(selectedValue => cities.find(_value => selectedValue === _value.name)?.name || '')}
                >
                    <SelectInput
                        label="City"
                        options={provinceCities}
                        placeholder="Search City"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={false}
                        value={filters.city}
                        onChange={val => setFilters(prev => ({ ...prev, city: val }))}
                        mode="multiple"
                    />
                </FilterItem>
            )}
            {filters.province.length > 0 && (
                <FilterItem
                    handleApply={handleApply}
                    label="Province"
                    onClear={() => handleClearFilter('province')}
                    value={values.province.map(selectedValue => pakStates.find(_value => selectedValue === _value.value)?.label || '')}
                >
                    <SelectInput
                        label="Province"
                        options={pakStates}
                        placeholder="Search City"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={false}
                        value={filters.province}
                        onChange={val => setFilters(prev => ({ ...prev, city: val }))}
                        mode="multiple"
                    />
                </FilterItem>
            )}
            {filters.segment.length > 0 && (
                <FilterItem
                    handleApply={handleApply}
                    label="Segment"
                    onClear={() => handleClearFilter('segment')}
                    value={values.segment.map(selectedValue => Object.values(SegmentEnumerator).find(_value => selectedValue === _value) || '')}
                >
                    <SelectInput
                        label="Segment"
                        options={Object.values(SegmentEnumerator).map(segment => ({
                            label: segment,
                            value: segment,
                        }))} // Mapping SegmentEnumerator to options
                        placeholder="Search Segment"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={false}
                        value={filters.segment}
                        onChange={val => setFilters(prev => ({ ...prev, segment: val }))}
                        mode="multiple"
                    />
                </FilterItem>
            )}
            {filters.productType.length > 0 && (
                <FilterItem handleApply={handleApply} label="Product" onClear={() => handleClearFilter('productType')} value={selectedProductType}>
                    <SelectInput
                        label="Product Type"
                        options={productType?.map(obj => ({ label: obj.name, value: obj.id }))} // Assuming productTypes is available
                        placeholder="Search Product Type"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={false}
                        value={filters.productType}
                        onChange={val => setFilters(prev => ({ ...prev, productType: val }))}
                        mode="multiple"
                    />
                </FilterItem>
            )}
            {filters.channel.length > 0 && (
                <FilterItem handleApply={handleApply} label="Channel" onClear={() => handleClearFilter('channel')} value={selectedChannels}>
                    <SelectInput
                        label="Channel"
                        options={channels.map(channel => ({
                            label: channel.name,
                            value: channel.id,
                        }))}
                        placeholder="Search Channel"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={false}
                        value={filters.channel}
                        onChange={val => setFilters(prev => ({ ...prev, channel: val }))}
                        mode="multiple"
                    />
                </FilterItem>
            )}
            {filters.quantity !== null && (
                <FilterItem handleApply={handleApply} label="Quantity" onClear={() => handleClearFilter('quantity')} value={values.quantity !== null ? [values.quantity?.toString()] : []}>
                    <QuantitySelect clasNames="!text-[#007AFF] !bg-white text-[12px]" isLabel={false} value={filters.quantity} onChange={val => setFilters(prev => ({ ...prev, quantity: val }))} />{' '}
                </FilterItem>
            )}
            {filters.brand.length > 0 && (
                <FilterItem handleApply={handleApply} label="Brand" onClear={() => handleClearFilter('brand')} value={selectedBrands}>
                    <SelectInput
                        label="Brand"
                        options={brands.map(brand => ({
                            label: brand.name,
                            value: brand.id,
                        }))}
                        placeholder="Search Brand"
                        clasNames="!text-[#007AFF] !bg-white text-[12px]"
                        isLabel={false}
                        value={filters.brand}
                        onChange={val => setFilters(prev => ({ ...prev, brand: val }))}
                        mode="multiple"
                    />
                </FilterItem>
            )}
            {filters.createdAt && (
                <FilterItem handleApply={handleApply} label="Date" onClear={() => handleClearFilter('createdAt')} value={[values.createdAt ?? '']}>
                    <DateSelect
                        placeholder="Select date"
                        label="Date"
                        clasNames="!text-[#007AFF] !bg-white text-[12px] leads-custom-date-selector"
                        isLabel={false}
                        value={filters.createdAt}
                        onChange={val => setFilters(prev => ({ ...prev, createdAt: val }))}
                    />
                </FilterItem>
            )}

            <div className={`${isFilterApllied ? 'flex' : 'hidden'} items-center  px-3 py-1  bg-white cursor-pointer`} onClick={handleClearAll}>
                <CrossIcon />
                <TextTypography fontSize={12} classNames=" !pl-2">
                    Clear All
                </TextTypography>
            </div>
        </div>
    );
};

interface IFilterItemProps {
    label: string;
    onClear: () => void;
    children: ReactNode;
    value: string[];
    handleApply: () => void;
}
const FilterItem: React.FC<IFilterItemProps> = ({ label, onClear, children, value, handleApply }: IFilterItemProps) => {
    const [open, setOpen] = useState<boolean>(false);

    const items: MenuProps['items'] = [
        {
            label: (
                <div className="flex">
                    {children}
                    <Button
                        className="bg-[#F2F2F2] text-[#4A4747]  px-5 rounded-lg w-[115px] h-[33px] hover:!text-[#4A4747] ml-3"
                        onClick={() => {
                            handleApply();
                            setOpen(false);
                        }}
                    >
                        Search
                    </Button>
                </div>
            ),
            key: '0',
        },
    ];

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setOpen(false);
        }, 1000);
    };

    return (
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <Dropdown menu={{ items }} trigger={['click']} open={open}>
                <a onClick={e => e.preventDefault()}>
                    <Space>
                        <div className="flex items-center bg-white px-2 py-1 rounded-lg border border-solid border-[#DBD7D7]">
                            <div
                                className="flex items-center cursor-pointer"
                                onClick={() => {
                                    onClear();
                                    setOpen(false);
                                }}
                            >
                                <CrossIcon />
                            </div>
                            <div onClick={() => setOpen(true)}>
                                <TextTypography fontSize={12} classNames="!pr-2 !pl-2">
                                    {label}
                                </TextTypography>
                                {value?.map(_val => (
                                    <span className="text-[12px] text-[#DBD7D7] ">
                                        |
                                        <TextTypography classNames="!text-[#007AFF] px-2" fontSize={12}>
                                            {_val}
                                        </TextTypography>
                                    </span>
                                ))}
                                <DownOutlined className="text-[12px]" />
                            </div>
                        </div>
                    </Space>
                </a>
            </Dropdown>
        </div>
    );
};

export default SelectedFilters;
